import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Avatar,
  Chip,
  Paper,
  Stack,
  Breadcrumbs,
  Link,
  Alert,
  CircularProgress,
} from '@mui/material';
// Note: Timeline components from @mui/lab need to be installed
// For now, using a simple list-based timeline
import {
  ArrowBack as BackIcon,
  Build as RevisionIcon,
  Person as PersonIcon,
  CheckCircle as CompleteIcon,
  Cancel as CancelIcon,
  Assignment as AssignIcon,
  PlayArrow as StartIcon,
  Devices as DeviceIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useApi } from '../../../hooks/useApi';
import { apiService } from '../../../services/api';
import { formatDate, formatTimeAgo, getDeviceDisplayName, getDeviceSubtitle } from '../../../utils/helpers';

interface DeviceRevisionHistoryProps {}

const DeviceRevisionHistory: React.FC<DeviceRevisionHistoryProps> = () => {
  const navigate = useNavigate();
  const { deviceId } = useParams<{ deviceId: string }>();

  const {
    data: revisionData,
    loading,
    error,
    execute: fetchRevisionHistory,
  } = useApi(() => deviceId ? apiService.getDeviceRevisionHistory(deviceId) : Promise.reject('No device ID'));

  useEffect(() => {
    if (deviceId) {
      fetchRevisionHistory();
    }
  }, [deviceId]);

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'created': return <StartIcon />;
      case 'assigned': return <AssignIcon />;
      case 'in_progress': return <RevisionIcon />;
      case 'completed': return <CompleteIcon />;
      case 'cancelled': return <CancelIcon />;
      default: return <RevisionIcon />;
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'created': return 'info';
      case 'assigned': return 'primary';
      case 'in_progress': return 'warning';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getActionLabel = (action: string) => {
    switch (action) {
      case 'created': return 'Revisión Creada';
      case 'assigned': return 'Técnico Asignado';
      case 'in_progress': return 'En Progreso';
      case 'completed': return 'Completada';
      case 'cancelled': return 'Cancelada';
      default: return action;
    }
  };

  const getReasonLabel = (reason: string) => {
    switch (reason) {
      case 'maintenance': return 'Mantenimiento';
      case 'client_request': return 'Solicitud Cliente';
      case 'quality_check': return 'Control Calidad';
      case 'malfunction': return 'Mal Funcionamiento';
      default: return reason;
    }
  };

  const getPriorityLabel = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'Urgente';
      case 'high': return 'Alta';
      case 'medium': return 'Media';
      case 'low': return 'Baja';
      default: return priority;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Error cargando historial de revisión: {error}
        <Button onClick={() => fetchRevisionHistory()} sx={{ ml: 2 }}>
          Reintentar
        </Button>
      </Alert>
    );
  }

  const device = revisionData?.device;
  const revisionInfo = revisionData?.revisionInfo;

  if (!device) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        Dispositivo no encontrado
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate('/devices/revisions')}
          >
            Revisiones
          </Link>
          <Typography variant="body2" color="text.primary">
            Historial
          </Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
              Historial de Revisión
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {getDeviceDisplayName(device)} - IMEI: {device.imei}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<BackIcon />}
            onClick={() => navigate('/devices/revisions')}
          >
            Volver
          </Button>
        </Box>
      </Box>

      {/* Device Info Card */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Avatar sx={{ width: 60, height: 60, bgcolor: 'primary.main' }}>
              <DeviceIcon fontSize="large" />
            </Avatar>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {getDeviceDisplayName(device)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {getDeviceSubtitle(device) || `IMEI: ${device.imei}`}
              </Typography>
            </Box>
            {revisionInfo?.isInRevision && (
              <Chip
                label="En Revisión"
                color="warning"
                variant="outlined"
                icon={<RevisionIcon />}
              />
            )}
          </Box>

          {revisionInfo && (
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <Chip
                label={getReasonLabel(revisionInfo.revisionReason)}
                color="primary"
                variant="outlined"
              />
              <Chip
                label={getPriorityLabel(revisionInfo.priority)}
                color={getPriorityColor(revisionInfo.priority) as any}
                variant="outlined"
              />
              {revisionInfo.assignedTechnicianId && (
                <Chip
                  label={`${revisionInfo.assignedTechnicianId.firstName} ${revisionInfo.assignedTechnicianId.lastName}`}
                  icon={<PersonIcon />}
                  color="info"
                  variant="outlined"
                />
              )}
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* Current Revision Info */}
      {revisionInfo && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Información de Revisión Actual
            </Typography>
            <Stack spacing={2}>
              {revisionInfo.revisionStartDate && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Fecha de Inicio
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(revisionInfo.revisionStartDate)}
                  </Typography>
                </Box>
              )}
              {revisionInfo.estimatedCompletionDate && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Fecha Estimada de Finalización
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(revisionInfo.estimatedCompletionDate)}
                  </Typography>
                </Box>
              )}
              {revisionInfo.revisionNotes && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Notas de Revisión
                  </Typography>
                  <Typography variant="body1">
                    {revisionInfo.revisionNotes}
                  </Typography>
                </Box>
              )}
              {revisionInfo.completionNotes && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Notas de Finalización
                  </Typography>
                  <Typography variant="body1">
                    {revisionInfo.completionNotes}
                  </Typography>
                </Box>
              )}
              {revisionInfo.cancellationReason && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Razón de Cancelación
                  </Typography>
                  <Typography variant="body1" color="error.main">
                    {revisionInfo.cancellationReason}
                  </Typography>
                </Box>
              )}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Timeline */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Historial de Acciones
          </Typography>

          {revisionInfo?.revisionHistory && revisionInfo.revisionHistory.length > 0 ? (
            <Stack spacing={2}>
              {revisionInfo.revisionHistory
                .sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime())
                .map((entry: any, index: number) => (
                <Paper key={index} elevation={3} sx={{ p: 3, position: 'relative' }}>
                  {/* Status icon */}
                  <Box sx={{
                    position: 'absolute',
                    top: 16,
                    left: 16,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    bgcolor: `${getActionColor(entry.action)}.main`,
                    color: 'white'
                  }}>
                    {getActionIcon(entry.action)}
                  </Box>

                  {/* Content */}
                  <Box sx={{ ml: 7 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {getActionLabel(entry.action)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatTimeAgo(entry.date)}
                      </Typography>
                    </Box>

                    {entry.performedBy && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Avatar sx={{ width: 24, height: 24, bgcolor: 'info.main' }}>
                          <PersonIcon fontSize="small" />
                        </Avatar>
                        <Typography variant="body2">
                          {entry.performedBy.firstName} {entry.performedBy.lastName}
                        </Typography>
                      </Box>
                    )}

                    <Typography variant="body2" color="text.secondary">
                      {formatDate(entry.date)}
                    </Typography>

                    {entry.notes && (
                      <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', bgcolor: 'grey.50', p: 1, borderRadius: 1 }}>
                        "{entry.notes}"
                      </Typography>
                    )}
                  </Box>
                </Paper>
              ))}
            </Stack>
          ) : (
            <Alert severity="info">
              No hay historial de revisiones para este dispositivo.
            </Alert>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default DeviceRevisionHistory;